import React, { useState } from 'react';
import styled from 'styled-components';
import { ActionButton, Buttons, CancelButton, Checkbox, Drawer, Form, HelpTooltip } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useFeatures, useForm } from '~/hooks';

const Checkboxes = styled.div`
  display: flex;
  flex-direction: column;

  > label:not(:last-child) {
    margin-bottom: 0.5rem;
  }
`;

const Notification = styled.div`
  display: flex;
  align-items: center;

  &:not(:last-child) {
    margin-bottom: 0.5rem;
  }
`;

const BudgetHelp = styled(HelpTooltip)`
  margin-left: 0.5rem;
`;

export default function TeamsNotificationsDrawer({ notifications: initialNotifications, onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const features = useFeatures();
  const [{ isSubmitting, saved }, form] = useForm();
  const [notifications, setNotifications] = useState(initialNotifications);
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);

  const handleChange = (event) => {
    setDirty(true);
    setNotifications((notifications) => ({ ...notifications, [event.target.name]: event.target.checked }));
  };

  return (
    <Drawer
      isOpen
      title="Microsoft Teams Notifications"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          form.submit();

          try {
            await api.www.workspaces(workspace.id).integrations().microsoft.teams.updateNotifications(notifications);
            form.save();
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        return (
          <>
            <Form.Section title="Notifications">
              <Form.Control>
                <Checkboxes>
                  <Checkbox
                    name="missingTimeReminders"
                    label="Missing time reminders"
                    checked={notifications.missingTimeReminders}
                    onChange={handleChange}
                  />
                  <Checkbox
                    name="unsubmittedTimeReminders"
                    label="Unsubmitted time reminders"
                    checked={notifications.unsubmittedTimeReminders}
                    onChange={handleChange}
                  />
                  {features.timesheets && (
                    <Checkbox
                      name="unsubmittedTimesheetReminders"
                      label="Unsubmitted timesheet reminders"
                      checked={notifications.unsubmittedTimesheetReminders}
                      onChange={handleChange}
                    />
                  )}
                  <Checkbox
                    name="timeExpenseApprovalReminders"
                    label="Time and expense approval reminders"
                    checked={notifications.timeExpenseApprovalReminders}
                    onChange={handleChange}
                  />
                  <Checkbox
                    name="timeExpenseRejected"
                    label="Time and expenses have been rejected"
                    checked={notifications.timeExpenseRejected}
                    onChange={handleChange}
                  />
                  <Notification>
                    <Checkbox
                      name="projectBudgetHoursWarning"
                      label="Project budget hours warning"
                      checked={notifications.projectBudgetHoursWarning}
                      onChange={handleChange}
                    />
                    <BudgetHelp message="A Teams notification will be sent when the budget used reaches 80% and 100%." />
                  </Notification>
                  <Checkbox
                    name="projectMemberAdded"
                    label="Project team member added"
                    checked={notifications.projectMemberAdded}
                    onChange={handleChange}
                  />
                  <Checkbox
                    name="projectStatusChanged"
                    label="Project status changed"
                    checked={notifications.projectStatusChanged}
                    onChange={handleChange}
                  />
                  <Checkbox
                    name="projectInvoicePublished"
                    label="Project invoice published"
                    checked={notifications.projectInvoicePublished}
                    onChange={handleChange}
                  />
                  <Checkbox
                    name="projectTaskStatusChanged"
                    label="Project task status changed"
                    checked={notifications.projectTaskStatusChanged}
                    onChange={handleChange}
                  />
                  <Notification>
                    <Checkbox
                      name="projectTaskBudgetExceeded"
                      label="Project task budget hours warning"
                      checked={notifications.projectTaskBudgetExceeded}
                      onChange={handleChange}
                    />
                    <BudgetHelp message="A Teams notification will be sent when the budget used reaches 80% and 100%." />
                  </Notification>
                  <Checkbox
                    name="clientApprovalSubmitted"
                    label="Client approval submitted"
                    checked={notifications.clientApprovalSubmitted}
                    onChange={handleChange}
                  />
                </Checkboxes>
              </Form.Control>
            </Form.Section>

            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}
